import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { FormHandles, Scope } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useModal } from 'react-brave-modal';
import { IoMdSearch } from 'react-icons/io';

import { usePatient } from '../../../hooks/patient';
import validationCPF from '../../../utils/validationCPF';

import getValidationErrors from '../../../utils/getValidationErrors';
import getGender from '../../../utils/getGender';
import { useAuth } from '../../../hooks/auth';
import {
  useCurrentCallback,
  useCurrentEffect,
} from '../../../lib/use-current-effect';
import { listAssignableInstitutions } from '../../../services/institution';
import { validatePatientDataService } from '../../../services/patient';
import useCep from '../../../hooks/cep';
import getUF from '../../../utils/getUF';

import DashboardTemplate from '../../../templates/Dashboard';
import InputMaskAlternative from '../../../components/InputMaskAlternative';
import InputWithoutTitle from '../../../components/InputWithoutTitle';
import PageTree from '../../../components/PageTree';
import { ElementPageTree } from '../../../components/PageTree/types';
import RequiredFieldInformation from '../../../components/RequiredFieldInformation';
import InputWithoutTitleAndFormattedToLower from '../../../components/InputWithoutTitleAndFormattedToLower';
import InputCheckWithoutForm from '../../../components/InputCheckWithoutForm';
import FinishPatientRegisterModal from './modals/FinishPatientRegisterModal';

import {
  AddressGrid,
  AddressToggle,
  Button,
  ButtonsContainer,
  Container,
  Field,
  FormContainer,
  GridContent,
  InputSelect,
} from './styles';
import MainAreasEnum from '../../../enums/MainAreasEnum';

/**
 * Endereço (0.31). Os sete campos vivem **dentro** de `personalData` na forma achatada
 * que o contrato publica (`addressUf`, não `address.uf`) — assimétrico com o
 * `POST /users`, que aninha, e mantido assim para não quebrar contrato.
 */
type AddressFormFields = {
  cep?: string;
  street?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  city?: string;
  addressUf?: string;
};

interface RegisterFormProps {
  personalData: {
    name: string;
    profileType: string;
    cpf: string;
    birthday?: string;
    gender?: 'M' | 'F' | 'ND';
    email: string;
    phone?: string;
  } & AddressFormFields;
  patientData: {
    referredBy?: string;
    bandId?: string;
  };
  /**
   * Conferência do e-mail. Fica **fora** do `Scope` de `personalData` de propósito:
   * é campo de tela, não dado do paciente, e o `celebrate` do `POST /patients` fecha
   * as chaves de `personalData` — uma chave a mais lá dentro seria 400.
   */
  emailConfirmation?: string;
}

type InputOptions = {
  label: string;
  value: string;
};

const Register: React.FC = () => {
  const history = useHistory();
  const { createPatient } = usePatient();
  const { showModal } = useModal();
  const formRef = useRef<FormHandles>(null);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [genders] = useState(getGender);
  const [ufProps] = useState(getUF);
  const { getAddressData, addressData, error: cepError } = useCep();

  /**
   * Revela os campos de endereço, no mesmo padrão do cadastro de equipe. Nasce
   * desmarcado, e enquanto estiver assim **nenhuma chave de endereço vai no corpo** — o
   * back grava `addresses` só quando algum campo vem preenchido, e mandar strings vazias
   * criaria uma linha indistinguível de um endereço em branco (é o defeito que o commit
   * `2f5c3bd1` corrigiu do lado do usuário de equipe).
   */
  const [showAddress, setShowAddress] = useState(false);

  const { user } = useAuth();

  /**
   * As instituições em que **este** usuário pode cadastrar (tarefa 0.30). Quem monta
   * a lista é o servidor, a partir do escopo do token: o `administrator` recebe todos
   * os municípios e todo outro papel recebe só o do próprio vínculo. Por isso a tela
   * não pergunta o papel de ninguém — se a opção está aqui, o `POST /patients` a
   * aceita.
   */
  const [institutions, setInstitutions] = useState<InputOptions[]>([]);

  /**
   * O `InputSelectAlternative` registra só `getValue` no unform, então
   * `formRef.setData` não alcança um select — e as opções chegam assíncronas, depois
   * do `defaultValue` do react-select. Estado local, com o select controlado, é o que
   * permite pré-selecionar a única opção de quem tem uma só.
   */
  const [selectedInstitution, setSelectedInstitution] = useState('');

  /**
   * Uma opção só significa "não há o que escolher": é o caso de todo papel de equipe,
   * que só cadastra na própria instituição. Trancar é mais honesto que um select
   * aberto com um item — e a regra de verdade está no back, que recusa com 403 id
   * fora do escopo.
   */
  const hasSingleInstitution = institutions.length === 1;

  useCurrentEffect((isCurrent) => {
    listAssignableInstitutions()
      .then((list) => {
        if (!isCurrent()) {
          return;
        }

        setInstitutions(
          list.map(({ id, name }) => ({ value: id, label: name })),
        );

        if (list.length === 1) {
          setSelectedInstitution(list[0].id);
        }
      })
      .catch(() => {
        isCurrent() &&
          toast.error(
            'Não foi possível carregar as instituições. Recarregue a página.',
          );
      });
  }, []);

  const handleCepSearch = useCallback(async () => {
    await getAddressData(formRef.current?.getFieldValue('personalData.cep'));
  }, [getAddressData]);

  useEffect(() => {
    if (addressData) {
      formRef.current?.setFieldValue('personalData.street', addressData.street);
      formRef.current?.setFieldValue(
        'personalData.neighborhood',
        addressData.neighborhood,
      );
      formRef.current?.setFieldValue('personalData.city', addressData.city);
      formRef.current?.setFieldValue('personalData.addressUf', addressData.uf);
    }
  }, [addressData]);

  useEffect(() => {
    if (cepError) {
      toast.error(cepError);
    }
  }, [cepError]);

  /**
   * Duplicidade conferida no `blur`, campo por campo (0.31).
   *
   * Antes disto o cadastro só descobria CPF ou e-mail repetido no **400 do submit**,
   * depois do formulário inteiro preenchido — e o CPF é o campo que mais dói, porque
   * quem já está na base não pode ser recadastrado, só transferido por um administrador.
   *
   * O erro vai para o próprio campo (não para um toast) porque é erro de campo. E é
   * `setFieldError`, não `setErrors`: este último **substitui** o mapa inteiro e apagaria
   * os erros dos outros campos.
   */
  const handleDuplicateCheck = useCurrentCallback(
    (isCurrent) => async (field: 'cpf' | 'email') => {
      const value = formRef.current
        ?.getFieldValue(`personalData.${field}`)
        ?.trim();

      if (!value) {
        return;
      }

      try {
        await validatePatientDataService({ [field]: value });

        if (isCurrent()) {
          formRef.current?.setFieldError(`personalData.${field}`, '');
        }
      } catch (err) {
        // Só o 400 desta rota é resposta de negócio ("já cadastrado", "CPF
        // inválido"). Rede fora do ar ou 403 não são defeito do que a pessoa digitou,
        // e marcar o campo de vermelho por isso mentiria — o submit ainda confere.
        if (isCurrent() && err.response?.status === 400) {
          formRef.current?.setFieldError(
            `personalData.${field}`,
            err.response.data.message,
          );
        }
      }
    },
    [],
  );

  const handleSubmit = useCurrentCallback(
    (isCurrent) => async (data: RegisterFormProps) => {
      setIsButtonDisabled(true);

      formRef.current?.setErrors({});

      try {
        const schema = Yup.object().shape({
          personalData: Yup.object().shape({
            name: Yup.string()
              .required('O nome é obrigatório')
              .test(
                'nome',
                'O nome deve ter pelo menos duas palavras',
                (value: string) => {
                  const words = value.trim().split(' ');
                  return words.length > 1 && words.every((i) => i);
                },
              ),
            cpf: Yup.string()
              .length(14, 'Digite um CPF válido')
              .test('cpf', 'Digite um CPF válido', (value) =>
                validationCPF(value),
              )
              .required('O CPF é obrigatório'),
            birthday: Yup.date().required('Data nascimento obrigatória'),
            /**
             * Obrigatório desde a 0.33, no formulário **e** no contrato: o e-mail é o
             * endereço do convite de boas-vindas e o **login** do paciente no aplicativo.
             * Cadastrar sem ele deixa o paciente sem caminho nenhum até a conta dele — que
             * é justamente o que a 0.33 conserta.
             */
            email: Yup.string()
              .email('Digite um email válido')
              .required('O e-mail é obrigatório'),
            phone: Yup.string()
              .length(16, 'Digite um número de telefone válido')
              .required('O telefone é obrigatório'),
          }),
          patientData: Yup.object().shape({
            referredBy: Yup.string(),
            bandId: Yup.string(),
          }),
          /**
           * Conferência do e-mail, para pegar o erro de digitação **antes** de gravar.
           * O e-mail do paciente é credencial (é por ele que o app faz login) e a
           * unicidade é conferida no back: um typo cria uma conta que ninguém acessa,
           * e o CPF já gravado impede recadastrar sem passar por edição.
           *
           * `oneOf` cobre os dois lados sem `when`: com os dois campos vazios os
           * valores são iguais e passa (o e-mail é opcional aqui); com um preenchido e
           * o outro não, difere e acusa.
           */
          emailConfirmation: Yup.string()
            .oneOf([Yup.ref('personalData.email')], 'Os e-mails não conferem')
            .required('Confirme o e-mail'),
          /**
           * Espelha a regra do back (0.30): a instituição é escolhida na tela, e o
           * escopo do token é quem autoriza a escolha. Exigir aqui evita descobrir no
           * 403 depois do formulário inteiro preenchido — e evita o silêncio pior, que
           * é o cadastro cair no tenant derivado sem ninguém ter escolhido.
           */
          institutionId: Yup.string().required('A instituição é obrigatória'),
        });

        // O select é controlado por estado, então o valor autoritativo é o do
        // componente — o `getData()` do unform devolveria o mesmo, mas depender dele
        // aqui acopla a validação ao registro do react-select.
        await schema.validate(
          { ...data, institutionId: selectedInstitution },
          {
            abortEarly: false,
          },
        );

        data.personalData.profileType = 'patient';

        const {
          cep,
          street,
          number,
          complement,
          neighborhood,
          city,
          addressUf,
          ...personalDataWithoutAddress
        } = data.personalData;

        const patientData = {
          type: 'patient',
          ...data,
          personalData: {
            ...personalDataWithoutAddress,
            email: data.personalData.email.trim().toLowerCase(),
            // Com o checkbox desmarcado as sete chaves **não vão** no corpo. Enviá-las
            // vazias faria o back gravar uma linha de `addresses` toda nula, que é
            // indistinguível de um endereço preenchido em branco.
            ...(showAddress
              ? {
                  cep,
                  street,
                  number,
                  complement,
                  neighborhood,
                  city,
                  addressUf,
                }
              : {}),
          },
          institutionId: selectedInstitution,
          createdUpdatedBy: user?.id,
        };

        await createPatient({ ...patientData });

        if (isCurrent()) {
          setIsButtonDisabled(false);
        }

        showModal({
          type: 'custom',
          data: (
            <FinishPatientRegisterModal
              institutionName={
                institutions.find(({ value }) => value === selectedInstitution)
                  ?.label
              }
              email={patientData.personalData.email}
            />
          ),
        });
      } catch (err) {
        if (isCurrent()) {
          setIsButtonDisabled(false);
        }

        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
        } else if (err.response) {
          toast.error(err.response.data.message);
        } else {
          toast.error('Entre em contato com o administrador.');
        }
      }
    },
    [
      createPatient,
      showModal,
      user,
      selectedInstitution,
      institutions,
      showAddress,
    ],
  );

  const pages = useMemo<ElementPageTree[]>(
    () => [
      { name: 'Pacientes', link: '/patient/list' },
      { name: 'Novo Paciente' },
    ],
    [],
  );

  return (
    <DashboardTemplate name={MainAreasEnum.PATIENTS}>
      <PageTree pages={pages} />
      <Container>
        <h1>Novo Paciente</h1>
        <FormContainer>
          <Form ref={formRef} onSubmit={handleSubmit}>
            <GridContent>
              <Scope path="personalData">
                <Field gridArea="no">
                  <label>
                    Nome
                    <RequiredFieldInformation />
                  </label>
                  <InputWithoutTitle name="name" />
                </Field>
                <Field gridArea="cpf">
                  <label>
                    CPF
                    <RequiredFieldInformation />
                  </label>
                  <InputMaskAlternative
                    type="text"
                    name="cpf"
                    title=""
                    mask="999.999.999-99"
                    placeholder="000.000.000-00"
                    onBlur={() => handleDuplicateCheck('cpf')}
                  />
                </Field>
                <Field gridArea="dn">
                  <label>
                    Data de Nascimento
                    <RequiredFieldInformation />
                  </label>
                  <InputWithoutTitle
                    className="datepicker"
                    type="date"
                    name="birthday"
                    max={new Date().toISOString().split('T')[0]}
                  />
                </Field>
                <Field gridArea="gn">
                  <label>Gênero</label>
                  <InputSelect name="gender" options={genders} placeholder="" />
                </Field>
                <Field gridArea="em">
                  <label>E-mail</label>
                  <InputWithoutTitleAndFormattedToLower
                    name="email"
                    onBlur={() => handleDuplicateCheck('email')}
                  />
                </Field>
                <Field gridArea="cl">
                  <label>
                    Celular (DDD + Número)
                    <RequiredFieldInformation />
                  </label>
                  <InputMaskAlternative
                    type="text"
                    name="phone"
                    title=""
                    mask="(99) 9 9999-9999"
                    placeholder="(00) 0 0000-0000"
                  />
                </Field>
              </Scope>
              {/*
                Fora do `Scope` de `personalData`: é conferência de digitação, não dado
                do paciente, e o `celebrate` do `POST /patients` fecha as chaves de
                `personalData` — dentro do escopo o campo iria no corpo e daria 400.
                Mesmo componente do e-mail para que a diferença de caixa não invente
                uma divergência: os dois chegam em minúsculas.
              */}
              <Field gridArea="ec">
                <label>Confirmar e-mail</label>
                <InputWithoutTitleAndFormattedToLower name="emailConfirmation" />
              </Field>
              {/*
                Instituição (tenant) do paciente — tarefa 0.30. As opções vêm do
                servidor já filtradas pelo escopo de quem está cadastrando: o
                `administrator` escolhe entre todas, e os outros papéis recebem uma
                opção só, que fica pré-selecionada e travada.
              */}
              <Field gridArea="in">
                <label>
                  Instituição
                  <RequiredFieldInformation />
                </label>
                <InputSelect
                  name="institutionId"
                  options={institutions}
                  placeholder=""
                  disabled={hasSingleInstitution}
                  // Controlado: o `InputSelectAlternative` só registra `getValue` no
                  // unform, então `setData` não alcança o select — e sem isto a única
                  // opção de quem tem uma só não apareceria escolhida.
                  value={
                    institutions.find(
                      ({ value }) => value === selectedInstitution,
                    ) ?? null
                  }
                  onChange={(option) =>
                    setSelectedInstitution(
                      (option as InputOptions)?.value ?? '',
                    )
                  }
                />
              </Field>
              {/* <Scope path="patientData">
                <Field gridArea="ep">
                  <label>Encaminhado por:</label>
                  <InputWithoutTitle name="referredBy" />
                </Field>
                <SearchButton gridArea="bu">
                  <img src={SearchIcon} alt="Buscar" />
                </SearchButton>
                <Field gridArea="idp">
                  <label>ID da Pulseira</label>
                  <InputWithoutTitle name="bandId" />
                </Field>
              </Scope>
              <SearchButton gridArea="but">
                <img src={SearchIcon} alt="Buscar" />
                Buscar
              </SearchButton> */}
            </GridContent>

            {/*
              Endereço (0.31). Os campos ficam dentro do mesmo `Scope` de `personalData`
              porque é lá que o contrato os espera (forma achatada, `addressUf`), mas em
              grid próprio: área declarada e não ocupada abre linha, então esconder
              metade do `GridContent` deixaria um buraco de três linhas.

              ⚠️ Até a 0.31 estes sete campos eram aceitos pela API e **descartados** —
              a listagem de pacientes tem colunas Estado e Cidade que nasciam vazias para
              todo paciente cadastrado por aqui. É também o endereço com **bairro** que a
              importação da base do município (F1.6) vai precisar.
            */}
            <AddressToggle>
              <InputCheckWithoutForm
                id="showAddress"
                checked={showAddress}
                onChange={(event) => setShowAddress(event.target.checked)}
              />
              <label htmlFor="showAddress">Informar endereço</label>
            </AddressToggle>

            {showAddress && (
              /*
                Segundo `Scope` com o **mesmo** path do primeiro: o unform prefixa os
                campos igual e o `getData()` funde os dois no mesmo objeto, então o
                endereço chega em `personalData.*` como o contrato espera — sem
                aninhar um `<Form>` dentro do outro, que é HTML inválido e deixaria os
                campos fora dos dados do formulário principal.
              */
              <Scope path="personalData">
                <AddressGrid>
                  <Field gridArea="ce">
                    <label>CEP</label>
                    <div className="cep">
                      <InputMaskAlternative
                        mask="99999-999"
                        placeholder="00000-000"
                        title=""
                        type="text"
                        name="cep"
                      />
                      <button type="button" onClick={handleCepSearch}>
                        <IoMdSearch />
                      </button>
                    </div>
                  </Field>
                  <Field gridArea="ed">
                    <label>Endereço</label>
                    <InputWithoutTitle name="street" />
                  </Field>
                  <Field gridArea="nm">
                    <label>Número</label>
                    <InputWithoutTitle name="number" type="number" />
                  </Field>
                  <Field gridArea="cm">
                    <label>Complemento</label>
                    <InputWithoutTitle name="complement" />
                  </Field>
                  <Field gridArea="ba">
                    <label>Bairro</label>
                    <InputWithoutTitle name="neighborhood" />
                  </Field>
                  <Field gridArea="cd">
                    <label>Cidade</label>
                    <InputWithoutTitle name="city" />
                  </Field>
                  <Field gridArea="uf">
                    <label>Estado</label>
                    <InputSelect
                      name="addressUf"
                      options={ufProps}
                      placeholder=""
                    />
                  </Field>
                </AddressGrid>
              </Scope>
            )}
          </Form>
        </FormContainer>
      </Container>
      <ButtonsContainer>
        <Button color="white" onClick={() => history.push('/patient/list')}>
          Cancelar
        </Button>
        <Button
          loading={isButtonDisabled}
          color="primary"
          onClick={() => formRef.current?.submitForm()}
          type="submit"
        >
          Cadastrar
        </Button>
      </ButtonsContainer>
    </DashboardTemplate>
  );
};

export default Register;
